/**
 * Multi-tenant domain configuration.
 *
 * Each domain hosts the SAME React app but presents a different brand,
 * hero copy, SEO meta and "focused service" depending on `window.location.hostname`.
 *
 * - digital-mallorca.de  → Main brand, full service catalogue
 * - other 6 domains      → Focused on one service, deep-linked + tailored hero
 *
 * Configuration is keyed by hostname (without protocol, without port, with optional `www.` stripped).
 */

const L = (de, en, es) => ({ de, en, es });

const MAIN_BRAND = {
  hostname: "digital-mallorca.de",
  isMain: true,
  brandName: "Mallorca Digital",
  brandTagline: "Tech Services",
  focusedServiceSlug: null,
  // Falls back to i18n hero defaults if null.
  hero: null,
  seo: {
    title: L(
      "Mallorca Digital & Tech Services · Starlink, Smart Home, WLAN, KI",
      "Mallorca Digital & Tech Services · Starlink, Smart Home, WiFi, AI",
      "Mallorca Digital & Tech Services · Starlink, Smart Home, WiFi, IA",
    ),
    description: L(
      "Premium Tech-Service auf Mallorca für deutsche Villa-Besitzer, Streamer und Digital Nomads. Starlink, Mesh-WLAN, Smart Home, KI-Automation, persönlicher Support.",
      "Premium tech service on Mallorca for German villa owners, streamers and digital nomads. Starlink, mesh WiFi, smart home, AI automation, personal support.",
      "Servicio tecnológico premium en Mallorca para propietarios de villas, streamers y nómadas digitales alemanes.",
    ),
  },
};

export const DOMAIN_CONFIG = {
  "digital-mallorca.de": MAIN_BRAND,

  "starlink-mallorca.de": {
    hostname: "starlink-mallorca.de",
    isMain: false,
    brandName: "Starlink Mallorca",
    brandTagline: "Installation & Service",
    focusedServiceSlug: "starlink",
    hero: {
      eyebrow: L(
        "Starlink Spezialist · Mallorca",
        "Starlink specialist · Mallorca",
        "Especialista Starlink · Mallorca",
      ),
      title: L(
        "Starlink Internet auf Mallorca – professionell installiert",
        "Starlink Internet on Mallorca – professionally installed",
        "Internet Starlink en Mallorca – instalación profesional",
      ),
      subtitle: L(
        "Bis zu 400 Mbit/s Satelliten-Internet für Villen, Fincas und entlegene Standorte – Standortanalyse, Montage und deutschsprachiger Support aus einer Hand.",
        "Up to 400 Mbps satellite internet for villas, fincas and remote locations – site analysis, mounting and German-speaking support.",
        "Hasta 400 Mbps de internet satelital para villas, fincas y zonas remotas – análisis, montaje y soporte en alemán.",
      ),
      cta1: L("Starlink jetzt anfragen", "Request Starlink now", "Solicitar Starlink"),
    },
    seo: {
      title: L(
        "Starlink Installation Mallorca · bis 400 Mbit/s · deutschsprachig",
        "Starlink installation Mallorca · up to 400 Mbps · German support",
        "Instalación Starlink Mallorca · hasta 400 Mbps · en alemán",
      ),
      description: L(
        "Professionelle Starlink-Installation auf ganz Mallorca: Standortanalyse, Montage, Mesh-WLAN, Tarifberatung. Bis 400 Mbit/s, niedrige Latenz, deutschsprachiger Support.",
        "Professional Starlink installation across Mallorca: site analysis, mounting, mesh WiFi, plan advice. Up to 400 Mbps, low latency, German support.",
        "Instalación profesional Starlink en toda Mallorca: análisis, montaje, WiFi mesh, asesoría. Hasta 400 Mbps, baja latencia, en alemán.",
      ),
    },
  },

  "internet-mallorca.de": {
    hostname: "internet-mallorca.de",
    isMain: false,
    brandName: "Internet Mallorca",
    brandTagline: "Starlink & Premium WLAN",
    focusedServiceSlug: "starlink",
    hero: {
      eyebrow: L("Schnelles Internet · überall auf Mallorca", "Fast internet · anywhere on Mallorca", "Internet rápido · en toda Mallorca"),
      title: L(
        "Stabiles Internet für jede Villa auf Mallorca",
        "Stable internet for every villa on Mallorca",
        "Internet estable para cada villa en Mallorca",
      ),
      subtitle: L(
        "Starlink, Glasfaser, LTE-Bonding und Premium-Mesh – wir liefern stabile Bandbreite an jeden Ort der Insel, selbst in Tramuntana und Pollença.",
        "Starlink, fibre, LTE bonding and premium mesh – stable bandwidth anywhere on Mallorca, even Tramuntana and Pollença.",
        "Starlink, fibra, LTE bonding y mesh premium – ancho de banda estable en cualquier punto de Mallorca.",
      ),
      cta1: L("Internet anfragen", "Request internet", "Solicitar internet"),
    },
    seo: {
      title: L(
        "Internet auf Mallorca · Starlink, Glasfaser, Premium WLAN",
        "Internet on Mallorca · Starlink, fibre, premium WiFi",
        "Internet en Mallorca · Starlink, fibra, WiFi premium",
      ),
      description: L(
        "Internet-Lösungen für Villen, Fincas und Unternehmer auf Mallorca: Starlink-Installation, Glasfaser-Beratung, Mesh-WLAN, LTE-Bonding. Deutschsprachiger Service.",
        "Internet solutions for villas, fincas and businesses on Mallorca: Starlink, fibre advice, mesh WiFi, LTE bonding. German support.",
        "Soluciones de internet para villas, fincas y empresas en Mallorca: Starlink, fibra, mesh, LTE bonding.",
      ),
    },
  },

  "kameramann-mallorca.de": {
    hostname: "kameramann-mallorca.de",
    isMain: false,
    brandName: "Kameramann Mallorca",
    brandTagline: "Camera Operator · Mallorca",
    focusedServiceSlug: "creator",
    hero: {
      eyebrow: L("Kameramann · Mallorca", "Camera operator · Mallorca", "Cámara · Mallorca"),
      title: L(
        "Dein Kameramann auf Mallorca – für Streamer, Vlogger und Marken",
        "Your camera operator on Mallorca – for streamers, vloggers and brands",
        "Tu cámara en Mallorca – para streamers, vloggers y marcas",
      ),
      subtitle: L(
        "Wir tragen das Rig, finden den perfekten Winkel und liefern TV-Qualität live – buchbar pro Stunde, Tag oder ganze Wochen-Tour.",
        "We carry the rig, find the perfect angle and deliver TV quality live – book hourly, daily or as a full weekly tour.",
        "Llevamos el rig, encontramos el ángulo perfecto y entregamos calidad TV en directo – por hora, día o tour semanal.",
      ),
      cta1: L("Kameramann buchen", "Book camera operator", "Reservar cámara"),
    },
    seo: {
      title: L(
        "Kameramann auf Mallorca buchen · IRL Streaming, Vlog, Brand-Content",
        "Camera operator on Mallorca · IRL streaming, vlog, brand content",
        "Cámara en Mallorca · streaming IRL, vlog, marca",
      ),
      description: L(
        "Professioneller Kameramann auf Mallorca für IRL-Streamer, YouTube-Creator und Marken. Mit Starlink, Gimbal, Wireless-Audio und Live-Encoder. Halbtag ab 250 €.",
        "Pro camera operator on Mallorca for IRL streamers, YouTube creators and brands. With Starlink, gimbal, wireless audio and live encoder. Half-day from 250 €.",
        "Cámara profesional en Mallorca para streamers IRL, creadores y marcas. Con Starlink, gimbal, audio inalámbrico y encoder.",
      ),
    },
  },

  "streamsetup-mallorca.de": {
    hostname: "streamsetup-mallorca.de",
    isMain: false,
    brandName: "Stream Setup Mallorca",
    brandTagline: "Pro Streaming Rigs",
    focusedServiceSlug: "creator",
    hero: {
      eyebrow: L("Pro Streaming Setup · Mallorca", "Pro streaming setup · Mallorca", "Setup streaming pro · Mallorca"),
      title: L(
        "Professionelles Stream-Setup auf Mallorca – Twitch, YouTube & Kick",
        "Professional stream setup on Mallorca – Twitch, YouTube & Kick",
        "Setup streaming profesional en Mallorca – Twitch, YouTube y Kick",
      ),
      subtitle: L(
        "Vom Streaming-PC bis zum mobilen IRL-Rig: wir planen, kaufen ein, verkabeln und konfigurieren – damit du dich aufs Streamen konzentrieren kannst.",
        "From streaming PC to mobile IRL rig: we plan, buy, cable and configure – so you can focus on streaming.",
        "Desde el PC de streaming al rig IRL móvil: planeamos, compramos, cableamos y configuramos.",
      ),
      cta1: L("Setup planen lassen", "Plan my setup", "Planificar setup"),
    },
    seo: {
      title: L(
        "Streaming Setup Mallorca · Twitch, YouTube, IRL Rigs vom Profi",
        "Streaming setup Mallorca · Twitch, YouTube, IRL rigs by pros",
        "Setup streaming Mallorca · Twitch, YouTube, IRL pro",
      ),
      description: L(
        "Professionelles Streaming-Setup für deine Villa oder mobil auf Mallorca: Kamera, Audio, Beleuchtung, OBS, Starlink-Anbindung – alles aus einer Hand.",
        "Professional streaming setup for your villa or mobile on Mallorca: camera, audio, lighting, OBS, Starlink – all from one source.",
        "Setup streaming profesional para tu villa o móvil en Mallorca: cámara, audio, iluminación, OBS, Starlink.",
      ),
    },
  },

  "irlstreams-mallorca.de": {
    hostname: "irlstreams-mallorca.de",
    isMain: false,
    brandName: "IRL Streams Mallorca",
    brandTagline: "Mobile Live Streaming",
    focusedServiceSlug: "creator",
    hero: {
      eyebrow: L("IRL Streaming · Mallorca", "IRL streaming · Mallorca", "IRL streaming · Mallorca"),
      title: L(
        "IRL-Streaming Setups & Operator – live aus ganz Mallorca",
        "IRL streaming setups & operator – live from anywhere on Mallorca",
        "Setups IRL & operador – en directo desde cualquier punto de Mallorca",
      ),
      subtitle: L(
        "Mobile Rucksack-Rigs mit Starlink Mini + LTE-Bonding, LiveU/MoneyPit-Encoder und Kameramann-Service. 99,9 % Uptime – auch in der Tramuntana.",
        "Mobile backpack rigs with Starlink Mini + LTE bonding, LiveU/MoneyPit encoder and camera operator. 99.9% uptime – even in Tramuntana.",
        "Rigs móviles con Starlink Mini + LTE bonding, encoder LiveU/MoneyPit y operador. 99,9 % uptime.",
      ),
      cta1: L("IRL-Rig anfragen", "Request IRL rig", "Solicitar rig IRL"),
    },
    seo: {
      title: L(
        "IRL Streaming Mallorca · mobile Rucksack-Rigs & Kameramann",
        "IRL streaming Mallorca · mobile backpack rigs & camera op",
        "Streaming IRL Mallorca · rigs móviles & cámara",
      ),
      description: L(
        "IRL-Live-Streaming auf Mallorca: Starlink Mini, LTE-Bonding, Encoder und Kameramann – buchbar pro Stunde oder Tag. Twitch, YouTube, Kick, TikTok Live.",
        "IRL live streaming on Mallorca: Starlink Mini, LTE bonding, encoder and camera op – hourly or daily. Twitch, YouTube, Kick, TikTok Live.",
        "Streaming IRL en directo en Mallorca: Starlink Mini, LTE bonding, encoder y cámara.",
      ),
    },
  },

  "onlinemarketing-mallorca.de": {
    hostname: "onlinemarketing-mallorca.de",
    isMain: false,
    brandName: "Online Marketing Mallorca",
    brandTagline: "Webseiten · SEO · KI-Automation",
    focusedServiceSlug: "digital",
    hero: {
      eyebrow: L("Online Marketing · Mallorca", "Online marketing · Mallorca", "Marketing online · Mallorca"),
      title: L(
        "Online Marketing auf Mallorca – Webseiten, SEO & KI-Automation",
        "Online marketing on Mallorca – websites, SEO & AI automation",
        "Marketing online en Mallorca – webs, SEO y automatización IA",
      ),
      subtitle: L(
        "Wir bringen deutschsprachige Unternehmer auf Mallorca bei Google nach oben: schnelle Webseiten, lokale SEO, Google Business und KI-Chatbots.",
        "We get German entrepreneurs on Mallorca to the top of Google: fast websites, local SEO, Google Business and AI chatbots.",
        "Posicionamos a empresarios alemanes en Mallorca en Google: webs rápidas, SEO local, Google Business y chatbots IA.",
      ),
      cta1: L("Beratung anfragen", "Request consultation", "Solicitar consulta"),
    },
    seo: {
      title: L(
        "Online Marketing Mallorca · SEO, Webseiten & KI für Unternehmer",
        "Online marketing Mallorca · SEO, websites & AI for businesses",
        "Marketing online Mallorca · SEO, webs e IA para empresas",
      ),
      description: L(
        "Online-Marketing für deutschsprachige Unternehmer auf Mallorca: moderne Webseiten, lokale SEO, Google Business Profil, KI-Chatbots und n8n-Automationen.",
        "Online marketing for German entrepreneurs on Mallorca: modern websites, local SEO, Google Business, AI chatbots, n8n automations.",
        "Marketing online para empresarios alemanes en Mallorca: webs modernas, SEO local, Google Business, chatbots IA.",
      ),
    },
  },
};

export const ALL_DOMAINS = Object.keys(DOMAIN_CONFIG);
export const MAIN_HOSTNAME = "digital-mallorca.de";

/** Strip protocol, port, www. Return canonical lower-cased hostname. */
export const normalizeHostname = (raw) => {
  if (!raw) return "";
  return raw.replace(/^https?:\/\//i, "").split("/")[0].split(":")[0].toLowerCase().replace(/^www\./, "");
};

/**
 * Resolves the active domain config from the current hostname.
 * Falls back to MAIN_BRAND when running on preview / localhost / unknown host.
 */
export const resolveDomainConfig = (hostname) => {
  const normalized = normalizeHostname(hostname);
  return DOMAIN_CONFIG[normalized] || MAIN_BRAND;
};
